/* eslint-disable no-multi-assign */
/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
/* eslint-disable no-console */
import * as Sentry from "@sentry/react"
import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"
import { ROOTS } from "hiya-common/config"
import { RootState, StoreType } from "hiya-common/src/stores"
import { useSelector } from "react-redux"
import { onlyToast } from "hiya-common/src/App/CustomToaster"
import { ApiHost } from "../constant"
import { isNode } from "./index"
import { logN } from "./log"

axios.defaults.timeout = 10000
class MyRequest {
  protected service: AxiosInstance = axios

  private static instance: MyRequest

  public IS_NODE: boolean

  public is_Proxy = false

  constructor() {
    this.IS_NODE = isNode()
    this.service = axios.create(this.requestConfig())
    this.interceptorsRequest()
    this.interceptorsResponse()
  }

  public log: any

  setPublicFunction({ log }) {
    this.log = log
  }

  /**
   * 初始化配置
   */
  protected requestConfig(): AxiosRequestConfig {
    if (this.IS_NODE) {
      this.is_Proxy = false
      return {
        baseURL: ROOTS.host[ENV],
        responseType: "json",
      }
    }
    try {
      const { proxyInfo } = window.APP_INITIAL_STATE.env.queries
      const proxy = JSON.parse(proxyInfo)
      this.is_Proxy = true

      // 安卓代理请求
      return {
        baseURL: `http://${proxy.ip}:${proxy.port}/proxyPost?url=${ROOTS.host[ENV].replace(".in", "").replace("http:", ENV === "production" ? "https:" : "http:")}`,
        responseType: "json",
      }
    } catch {
      return {
        baseURL: `${window.location.origin}/api/proxy?url=${ROOTS.host[ENV]}`,
        responseType: "json",
      }
    }
  }

  private captureProxyRequestIssue(reason: string, extra?: Record<string, unknown>): void {
    if (this.IS_NODE || !this.is_Proxy) {
      return
    }
    // 埋点 /stat/action 失败（超时等）噪音大，不上报 Sentry
    const url = typeof extra?.url === "string" ? extra.url : ""
    if (url.includes("/stat/action")) {
      return
    }
    Sentry.captureEvent({
      message: reason,
      level: "error",
      extra,
    })
  }

  /**
   * 请求拦截
   * 1.添加公共参数
   */
  protected interceptorsRequest(): void {
    this.service.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        if (config.store) {
          const {
            env: { deviceinfo, clientIp },
          } = config.store.getState()
          // 自动取deviceinfo
          Object.assign(config, {
            data: {
              web_ts: +new Date(),
              ...config.data,
              ...deviceinfo,
            },
          })
          if (clientIp) {
            // 携带用户真实ip
            config.headers["X-Forwarded-For"] = config.headers["X-Real-IP"] = clientIp
          }
        }
        Object.assign(config, {
          metadata: { startTime: new Date().valueOf() },
        })
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  /**
   * 响应拦截
   * 1.打印日志
   * 2.统一处理
   */
  protected interceptorsResponse(): void {
    this.service.interceptors.response.use(
      (response: AxiosResponse) => {
        return this.handleResponse(response)
      },
      (error) => {
        const { response } = error
        if (response === undefined) {
          const cfg = error?.config
          const extra: Record<string, unknown> = {
            phase: "network",
            errorMessage: error?.message ?? String(error),
          }
          if (cfg) {
            extra.url = `${cfg.baseURL ?? ""}${cfg.url ?? ""}`
            if (cfg.method) {
              extra.method = cfg.method
            }
          }
          if (error?.code !== undefined) {
            extra.errorCode = error.code
          }
          this.captureProxyRequestIssue("HTTP请求异常(代理1)", extra)
          return Promise.reject(new Error(error))
        }
        return this.handleResponse(response)
      }
    )
  }

  protected handleResponse(response: AxiosResponse): Promise<AxiosResponse<any>> {
    if (response.config.xcHideLog) {
      return Promise.resolve(response)
    }
    const { data, status, config, statusText } = response
    const succeed = data.ret === 1 && data.data
    this.responseLog(response, succeed)
    if (succeed) {
      return Promise.resolve(response)
    }
    const msg = data?.msg || data?.err_msg || data?.data?.err_msg
    if (response.config.xcErrorToast && msg) {
      onlyToast(msg)
      this.captureProxyRequestIssue("展示toast-接口业务错误(代理)", {
        phase: "business",
        url: `${config.baseURL}${config.url}`,
        httpStatus: status,
        statusText,
        bizMsg: msg,
        responseBody: data,
      })
    }
    if (status !== 200) {
      this.captureProxyRequestIssue("接口业务错误(代理1)", {
        phase: "business",
        url: `${config.baseURL}${config.url}`,
        httpStatus: status,
        statusText,
        bizMsg: msg,
        responseBody: data,
      })
    }
    return Promise.reject(msg)
  }

  protected responseLog(response: AxiosResponse, succeed: boolean) {
    response.config.metadata.endTime = new Date().valueOf()
    const req = JSON.parse(response.config.data)
    const info: any = {
      mid: req.h_m,
      ip: response.config.headers["X-Forwarded-For"],
      type: this.IS_NODE ? "fetchData" : "http",
      url: `${response.config.baseURL}${response.config.url}`,
      method: response.config.method,
      req,
      res: response.data,
      status: response.status,
      ua: response.config.headers["User-Agent"],
      responseTime: response.config.metadata.endTime - response.config.metadata.startTime,
    }
    this.log[succeed ? "info" : "error"](info)
  }

  /**
   * 创建唯一实例（单例模式）
   */
  public static getInstance(): MyRequest {
    if (!this.instance) {
      this.instance = new MyRequest()
    }
    return this.instance
  }

  public post<T = any>(url: string, data: any = {}, config: AxiosRequestConfig = {}) {
    return this.service.post<T>(url, data, config)
  }

  public outPost<T = any>(url: string, data: any = {}, config: AxiosRequestConfig = {}) {
    return this.service.post<T>(url, data, {
      baseURL: ApiHost,
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
      ...config,
    })
  }

  public get<T = any>(url: string, params: any = {}, config: AxiosRequestConfig = {}) {
    return this.service.get<T>(url, {
      params,
      ...config,
    })
  }
}

export const http = MyRequest.getInstance()
const logFun =
  ({ deviceinfo, clientIp, userAgent, queries, path }: RootState["env"]) =>
    ({
      action,
      otype,
      data,
      id,
      oid,
      src = "hiya",
      mid,
      did,
    }: {
      action: string
      otype: string
      data?: any
      id?: any
      oid?: any
      src?: string
      mid?: string
      did?: string
    }) => {
      const commonData = {
        app_name: "hiya",
        ip: clientIp,
        ua: userAgent,
        path,
        // 全部添加ab实验client_id  默认为 ‘’ 没有不穿设置为undefined
        // client_id: clientId || undefined,
        ...(queries.share_to && { shareTo: queries.share_to }),
        ...(queries.m && { mid: queries.m }),
        ...(queries.d && { did: queries.d }),
        ...(queries.nt && { netType: queries.nt }),
      }
      try {
        if (ENV !== "production") {
          logN.success("埋点", otype, {
            action,
            otype,
            data: {
              mid: deviceinfo.h_m,
              ...data,
              st: Date.now(),
              // eslint-disable-next-line no-plusplus
              logId: `${Math.floor(Date.now() / 1000)}`,
            },
            ...(id && { id }),
            ...(oid && { oid }),
            ...(src && { src }),
          })
        }
      } catch (e) {
        console.log(e)
      }
      return http.post(
        "/stat/action",
        {
          ...commonData,
          ...deviceinfo,
          list: [
            {
              action,
              otype,
              data: {
                mid: deviceinfo.h_m,
                ...data,
                st: Date.now(),
                // eslint-disable-next-line no-plusplus
                logId: `${Math.floor(Date.now() / 1000)}`,
              },
              ...(id && { id }),
              ...(oid && { oid }),
              ...(src && { src }),
            },
          ],
          ...(mid && { h_m: Number(mid) }),
          ...(did && { h_did: did }),
        },
        {
          baseURL: `${window.location.origin}/host`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
          },
          xcHideLog: true,
        }
      )
    }
export const log = createAsyncThunk<
  void,
  {
    action: string
    otype: string
    data?: any
    id?: any
    oid?: any
    src?: string
    mid?: string
    did?: string
  }
>("log", async (arg, store) => {
  await logFun(store.getState().env)(arg)
})
export function useLog() {
  const env = useSelector((state: any) => {
    return state.env
  })
  return logFun(env)
}
